import React from "react";

// region Imports - Atoms
import AccordionSummaryChips from "@atoms/AccordionSummaryChips";
// endregion Imports - Atoms
// region Imports - Molecules
import KanbanAccordionCard from "@molecules/KanbanAccordionCard";
// endregion Imports - Molecules
// region Imports - Organisms
import { INumberRangeValues } from "@organisms/GenericQueryFilter";
// endregion Imports - Organisms
// region Imports - Libraries
import { TextField } from "@libraries/mui";
import _ from "lodash";
// endregion Imports - Libraries
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { FilterMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Styles
import * as Styled from "./styles";
// endregion Imports - Styles

// region Interfaces

interface IProps {
  label: string;
  onChangeNumberRange?: (values: INumberRangeValues) => void;
  defaultValues: INumberRangeValues;
}

// endregion Interfaces

const KanbanAccordionCardFilterNumberRange: React.FC<IProps> = ({
  label,
  onChangeNumberRange,
  defaultValues
}) => {
  const { t } = useTranslation();

  // State to control accordion expansion
  const [expanded, setExpanded] = React.useState<boolean>(false);

  // States for minimum and maximum values
  const [minValue, setMinValue] = React.useState<string>(defaultValues.min);
  const [maxValue, setMaxValue] = React.useState<string>(defaultValues.max);

  /**
   * Handles the accordion expansion/collapse.
   * @param _event The event
   * @param isExpanded If the card is expanded or not
   */
  const handleAccordionChange = (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded(isExpanded);
  };

  /**
   * Handles changes in the number input fields.
   * @param event The change event
   */
  const handleValueChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    if (name === "min") {
      setMinValue(value);
      if (onChangeNumberRange) {
        onChangeNumberRange({ min: value, max: maxValue });
      }
    } else if (name === "max") {
      setMaxValue(value);
      if (onChangeNumberRange) {
        onChangeNumberRange({ min: minValue, max: value });
      }
    }
  };

  // Update the values when defaultValues change
  React.useEffect(() => {
    if (_.isEqual(defaultValues, { min: minValue, max: maxValue })) return;
    setMinValue(defaultValues.min);
    setMaxValue(defaultValues.max);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  return (
    <Styled.Container>
      <KanbanAccordionCard
        onChange={handleAccordionChange}
        showExpandIcon
        accordionSummary={
          <AccordionSummaryChips
            label={label}
            chips={[]}
            onDeleteChip={() => {}}
          />
        }
        expanded={expanded}
      >
        <Styled.AccordionDetail>
          {expanded && (
            <>
              <TextField
                name="min"
                label={t(FilterMessages.optionNumberStartValue)}
                fullWidth
                type="number"
                value={minValue}
                onChange={handleValueChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                name="max"
                label={t(FilterMessages.optionNumberEndValue)}
                fullWidth
                type="number"
                value={maxValue}
                onChange={handleValueChange}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
        </Styled.AccordionDetail>
      </KanbanAccordionCard>
    </Styled.Container>
  );
};

KanbanAccordionCardFilterNumberRange.defaultProps = {
  onChangeNumberRange: () => {},
  defaultValues: {
    min: "",
    max: ""
  }
};

export default KanbanAccordionCardFilterNumberRange;
